import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  DirectionsCar,
  CloudQueue,
  Speed,
  Analytics,
  Security,
  Assessment,
  CheckCircle,
  ArrowForward,
  Person,
  Business,
  DataUsage,
  Tv
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const Automotive = () => {
  const caseStudies = [
    {
      title: 'Connected Vehicle Data Platform',
      description: 'A leading automaker implemented a comprehensive IoT platform to collect, process, and analyze real-time vehicle data for predictive maintenance and enhanced customer experience.',
      link: '#'
    },
    {
      title: 'Manufacturing Digital Transformation',
      description: 'A global automotive manufacturer modernized their production systems with AI-driven quality control and automated supply chain management.',
      link: '#'
    },
    {
      title: 'Autonomous Vehicle Infrastructure',
      description: 'A major automotive company built the cloud infrastructure and data processing capabilities needed to support autonomous vehicle testing and deployment.',
      link: '#'
    }
  ];

  const keyStats = [
    { value: '40%', description: 'reduction in manufacturing costs through digital transformation initiatives.' },
    { value: '99.9%', description: 'uptime achieved for critical automotive manufacturing systems.' },
    { value: '60%', description: 'faster time-to-market for new vehicle models through cloud-native development.' }
  ];

  const services = [
    {
      icon: <CloudQueue sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Cloud Integration',
      description: 'Seamless cloud migration and hybrid cloud solutions for automotive operations.'
    },
    {
      icon: <Tv sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'IoT & Connected Vehicles',
      description: 'End-to-end IoT solutions for connected vehicle data collection and analysis.'
    },
    {
      icon: <Analytics sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Data Analytics & AI',
      description: 'Advanced analytics for predictive maintenance, quality control, and customer insights.'
    },
    {
      icon: <Security sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Cybersecurity',
      description: 'Comprehensive security solutions for connected vehicles and manufacturing systems.'
    }
  ];

  const expert = {
    name: 'Michael Rodriguez',
    title: 'Vice President, Automotive',
    company: 'Scherstellen Technologie',
    image: '/api/placeholder/150/150',
    qa: [
      {
        question: 'How are automakers leveraging cloud technologies to transform their operations?',
        answer: 'Automakers are using cloud platforms to modernize legacy systems, enable real-time data processing, and support new business models like connected services and autonomous vehicles. Cloud technology provides the scalability and flexibility needed for rapid innovation.'
      },
      {
        question: 'What role does data analytics play in automotive manufacturing?',
        answer: 'Data analytics is crucial for predictive maintenance, quality control, and supply chain optimization. By analyzing production data in real-time, manufacturers can reduce downtime, improve quality, and optimize their operations for maximum efficiency.'
      },
      {
        question: 'How are automotive companies addressing cybersecurity challenges in connected vehicles?',
        answer: 'Automotive companies are implementing comprehensive cybersecurity frameworks that include secure communication protocols, over-the-air updates, and continuous monitoring to protect connected vehicles from cyber threats.'
      }
    ]
  };

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 12,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 'bold',
                color: '#1a1a1a',
                mb: 3,
                lineHeight: 1.2
              }}
            >
              Automotive
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: '#1976d2',
                mb: 2,
                fontWeight: 'bold'
              }}
            >
              Helping automakers embrace cloud technologies
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: '#666',
                mb: 4,
                lineHeight: 1.6,
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              Better connect data across mission-critical operations and drive innovation in the automotive industry
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Overview Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: '#1a1a1a',
                  mb: 3
                }}
              >
                Overview
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#666',
                  mb: 4,
                  lineHeight: 1.6
                }}
              >
                Scherstellen Technologie helps automotive companies modernize their IT infrastructure, embrace cloud technologies, and better connect data across mission-critical operations. We enable automakers to accelerate innovation, improve manufacturing efficiency, and deliver exceptional customer experiences.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: 400,
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Automotive Technology Solutions
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Case Studies Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Success Stories
          </Typography>
          <Grid container spacing={4}>
            {caseStudies.map((study, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {study.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {study.description}
                    </Typography>
                    <Button
                      variant="outlined"
                      endIcon={<ArrowForward />}
                      sx={{
                        borderColor: '#1976d2',
                        color: '#1976d2',
                        '&:hover': {
                          borderColor: '#1565c0',
                          color: '#1565c0',
                          bgcolor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                    >
                      Learn more
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Key Statistics */}
      <Box sx={{ py: 10, bgcolor: '#1976d2', color: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              mb: 6,
              textAlign: 'center'
            }}
          >
            3 things to know
          </Typography>
          <Grid container spacing={4}>
            {keyStats.map((stat, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 'bold',
                      mb: 2
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {stat.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Our Automotive Solutions
          </Typography>
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      {service.icon}
                      <Typography
                        variant="h5"
                        sx={{
                          ml: 2,
                          fontWeight: 'bold',
                          color: '#1a1a1a'
                        }}
                      >
                        {service.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        lineHeight: 1.6
                      }}
                    >
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Expert Q&A Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Industry Experts
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Our experts, your challenges
          </Typography>
          
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  src={expert.image}
                  sx={{
                    width: 150,
                    height: 150,
                    mx: 'auto',
                    mb: 3
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: '#1a1a1a',
                    mb: 1
                  }}
                >
                  {expert.name}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#1976d2',
                    mb: 1
                  }}
                >
                  {expert.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#666'
                  }}
                >
                  {expert.company}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: '#1a1a1a',
                    mb: 4
                  }}
                >
                  Q&A with Scherstellen Technologie automotive expert, {expert.name}
                </Typography>
                
                {expert.qa.map((qa, index) => (
                  <Box key={index} sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1976d2',
                        mb: 2
                      }}
                    >
                      Q: {qa.question}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        lineHeight: 1.6,
                        mb: 3
                      }}
                    >
                      A: {qa.answer}
                    </Typography>
                    {index < expert.qa.length - 1 && <Divider sx={{ my: 2 }} />}
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: '#1976d2',
              color: 'white',
              borderRadius: 2
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Connect with an expert to discuss your IT Transformation
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Let's help you modernize and transform your automotive operations
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Contact Us
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default Automotive;
